import { useLazyQuery } from '@apollo/client'
import { BoxLockVariationType } from '@jusbrasil-web/shared-apollo-client/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useContext, useEffect, useRef } from 'react'

import { BoxLockQueryQuery, BoxLockQueryQueryVariables } from './__generated__/query.graphql'
import {
  AiOfferLock,
  AiOfferSwitchFrequencyLock,
  AiTwoOffersLock,
  BoxLockContext,
  CaseTrackingBgCheckFromBgCheckOffersLock,
  CaseTrackingBGCheckFromLawsuitOffersLock,
  CustomPlanLock,
  DoctrineOneOfferLock,
  DoctrineUnloggedLock,
  DynamicMultipleOffersChooseFrequencyLaterLock,
  DynamicMultipleOffersSwitchFrequencyLock,
  DynamicMultipleOffersSwitchFrequencyNewBenefitsLock,
  InconsistentPlanLock,
  JurisThreeOffersSwitchFrequencyLock,
  LawsuitNameMonitoringLock,
  LawsuitTwoOffersLock,
  LimitOverflowLock,
  LinkedAccountLock,
  OneOfferLock,
  PayPerViewLock,
  PixUpgradeLock,
  PreLoadingModal,
  ThirdPartyBackgroundCheckLock,
  TwoOffersLock,
  TwoOffersSwitchFrequencyLock,
  UpdatePaymentInfoLock,
  UpgradePlanLock,
} from './components'
import { useRegisterLockEvent } from './hooks'
import { BoxLockQuery } from './query'
import { BoxLockWithProviderProps, LockEventName } from './types'

// TODO: Fix yearly experiment behavior of sending 4 events on render. Before rollout, we need to think a better approach for this logic @augusto
// The ideia is let the component itself handle with lock events.
export const variantEventsBlockedOnProvider: BoxLockVariationType[] = [
  BoxLockVariationType.JurisTwoOffersSwitchFrequencyLock,
  BoxLockVariationType.JurisThreeOffersSwitchFrequencyLock,
  BoxLockVariationType.LawsuitTwoOffersLock,
  BoxLockVariationType.DoctrineTwoOffersSwitchFrequencyLock,
  BoxLockVariationType.DynamicMultipleOffersChooseFrequencyLaterLock,
]

export function BoxLockWithProvider(props: BoxLockWithProviderProps) {
  const { appId, featureCode, fromComponent, adminOptions, boxOptions, pageMetadata, setContext } =
    props
  const [fetchQuery, { data, loading }] = useLazyQuery<
    BoxLockQueryQuery,
    BoxLockQueryQueryVariables
  >(BoxLockQuery, {
    variables: {
      appId,
      featureCode,
      fromComponent,
      adminOptions,
      boxOptions,
      pageMetadata,
    },
  })

  const context = useContext(BoxLockContext)
  const { open, prefetch, setStatus, componentType } = context

  const isSuccessModalOpen =
    context.componentType === 'modal' && 'isSuccessModalOpen' in context
      ? context.isSuccessModalOpen
      : false

  const { registerLockEvent } = useRegisterLockEvent()
  const hasReportedUnrecognizedLock = useRef(false)

  useEffect(() => {
    if (context.componentType === 'modal') {
      setContext?.(context)
    }
  }, [context, setContext])

  useEffect(() => {
    if (prefetch) {
      fetchQuery()
    }
  }, [fetchQuery, prefetch])

  const hasData = !!data
  useEffect(() => {
    if (open) {
      if (hasData) {
        setStatus('ready')
      } else {
        fetchQuery()
      }
    }
  }, [fetchQuery, hasData, open, setStatus])

  const { lockVariationType, offerLockData } = data?.root.boxLockViewer || {}
  const { viewer } = data?.root || {}

  useEffect(() => {
    if (open && offerLockData) {
      const isEventSendingBlockedOnProvider =
        lockVariationType && variantEventsBlockedOnProvider.includes(lockVariationType)
      if (!isEventSendingBlockedOnProvider) {
        const eventProperties = offerLockData.body.cards.map((card) => card.lockEventProperties)
        registerLockEvent({
          eventName: LockEventName.LOCK_VIEW,
          eventProperties,
        })
      }
    }
  }, [offerLockData, open, lockVariationType, registerLockEvent])

  if (open && loading && componentType === 'modal') {
    return <PreLoadingModal />
  }

  if (isSuccessModalOpen || !open || !data || loading) {
    return null
  }

  const LockComponent = getLockComponent({
    lockVariationType,
    offerLockData,
    viewer,
    featureCode,
    componentType,
  })

  if (open && hasData && !LockComponent && !hasReportedUnrecognizedLock.current) {
    hasReportedUnrecognizedLock.current = true
    sendMetrics('Box.UnrecognizedLockType', viewer?.pid ? String(viewer.pid) : null, {
      app_id: appId,
      from_component: fromComponent,
      feature: featureCode,
      modal_type: lockVariationType,
      source: 'BoxLockWithProvider',
      resource: componentType,
    })
  }

  return LockComponent
}

type GetLockComponentProps = {
  lockVariationType: BoxLockVariationType | null | undefined
  viewer: BoxLockQueryQuery['root']['viewer'] | null | undefined
  offerLockData:
    | NonNullable<BoxLockQueryQuery['root']['boxLockViewer']>['offerLockData']
    | null
    | undefined
  featureCode: string
  componentType: string
}

const getLockComponent = ({
  lockVariationType,
  componentType,
  offerLockData,
  viewer,
  featureCode,
}: GetLockComponentProps) => {
  if (offerLockData && viewer) {
    if (componentType === 'modal') {
      switch (lockVariationType) {
        case BoxLockVariationType.UpdatePaymentInfoLock:
          return <UpdatePaymentInfoLock offerLockData={offerLockData} />
        case BoxLockVariationType.FeatureLockLinkedAccountLock:
          return <LinkedAccountLock offerLockData={offerLockData} featureCode={featureCode} />
        case BoxLockVariationType.InconsistentLock:
          return <InconsistentPlanLock offerLockData={offerLockData} />
        case BoxLockVariationType.LimitOverflowLock:
          return <LimitOverflowLock offerLockData={offerLockData} />
        case BoxLockVariationType.JurisTwoOffersLock:
          return <TwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JurisTwoOffersSwitchFrequencyLock:
          return <TwoOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineTwoOffersSwitchFrequencyLock:
          return <TwoOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitOneOfferLock:
          return <OneOfferLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.UpgradeLock:
          return <UpgradePlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineOneOfferLock:
          return <DoctrineOneOfferLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineUnloggedLock:
          return <DoctrineUnloggedLock offerLockData={offerLockData} />
        case BoxLockVariationType.AiOfferLock:
          return <AiOfferLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JusIaTwoOffersLock:
          return <AiTwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitTwoOffersLock:
          return <LawsuitTwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitNameMonitoringLock:
          return <LawsuitNameMonitoringLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.AiOfferSwitchFrequencyLock:
          return <AiOfferSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.ThirdPartyBackgroundCheckTwoOffersLock:
          return <ThirdPartyBackgroundCheckLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.LawsuitPayPerViewLock:
          return <PayPerViewLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.PixUpgradeLock:
          return <PixUpgradeLock offerLockData={offerLockData} />
        case BoxLockVariationType.CustomPlanLock:
          return <CustomPlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JurisThreeOffersSwitchFrequencyLock:
          return (
            <JurisThreeOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
          )
        case BoxLockVariationType.CaseTrackingBgCheckFromBgCheckOffersLock:
          return (
            <CaseTrackingBgCheckFromBgCheckOffersLock
              offerLockData={offerLockData}
              viewer={viewer}
              lockVariationType={lockVariationType}
            />
          )
        case BoxLockVariationType.CaseTrackingBgCheckFromLawsuitOffersLock:
          return (
            <CaseTrackingBGCheckFromLawsuitOffersLock
              offerLockData={offerLockData}
              viewer={viewer}
              lockVariationType={lockVariationType}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyNewBenefitsLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyNewBenefitsLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersChooseFrequencyLaterLock:
          return (
            <DynamicMultipleOffersChooseFrequencyLaterLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
      }
    } else if (componentType === 'paywall') {
      switch (lockVariationType) {
        case BoxLockVariationType.UpdatePaymentInfoLock:
          return <UpdatePaymentInfoLock offerLockData={offerLockData} />
        case BoxLockVariationType.FeatureLockLinkedAccountLock:
          return <LinkedAccountLock offerLockData={offerLockData} featureCode={featureCode} />
        case BoxLockVariationType.InconsistentLock:
          return <InconsistentPlanLock offerLockData={offerLockData} />
        case BoxLockVariationType.LimitOverflowLock:
          return <LimitOverflowLock offerLockData={offerLockData} />
        case BoxLockVariationType.JurisTwoOffersLock:
          return <TwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JurisTwoOffersSwitchFrequencyLock:
          return <TwoOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineTwoOffersSwitchFrequencyLock:
          return <TwoOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineOneOfferLock:
          return <DoctrineOneOfferLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.DoctrineUnloggedLock:
          return <DoctrineUnloggedLock offerLockData={offerLockData} />
        case BoxLockVariationType.UpgradeLock:
          return <UpgradePlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JusIaTwoOffersLock:
          return <AiTwoOffersLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.PixUpgradeLock:
          return <PixUpgradeLock offerLockData={offerLockData} />
        case BoxLockVariationType.CustomPlanLock:
          return <CustomPlanLock offerLockData={offerLockData} viewer={viewer} />
        case BoxLockVariationType.JurisThreeOffersSwitchFrequencyLock:
          return (
            <JurisThreeOffersSwitchFrequencyLock offerLockData={offerLockData} viewer={viewer} />
          )
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersSwitchFrequencyNewBenefitsLock:
          return (
            <DynamicMultipleOffersSwitchFrequencyNewBenefitsLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
        case BoxLockVariationType.DynamicMultipleOffersChooseFrequencyLaterLock:
          return (
            <DynamicMultipleOffersChooseFrequencyLaterLock
              offerLockData={offerLockData}
              viewer={viewer}
            />
          )
      }
    }
  }

  if (offerLockData && !viewer) {
    if (componentType === 'paywall') {
      switch (lockVariationType) {
        case BoxLockVariationType.DoctrineOneOfferLock:
          return <DoctrineOneOfferLock offerLockData={offerLockData} />
        case BoxLockVariationType.DoctrineUnloggedLock:
          return <DoctrineUnloggedLock offerLockData={offerLockData} />
      }
    }
    if (componentType === 'modal') {
      switch (lockVariationType) {
        case BoxLockVariationType.AiOfferLock:
          return <AiOfferLock offerLockData={offerLockData} />
        case BoxLockVariationType.AiOfferSwitchFrequencyLock:
          return <AiOfferSwitchFrequencyLock offerLockData={offerLockData} />
      }
    }
  }
  return null
}
